import { useEffect, type ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Button, IconButton, Stack } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import { ResourceContextProvider } from 'ra-core';
import { EmailRichTextInput } from '@/modules/quotes/EmailRichTextInput';

type EditableEstimateSectionProps = {
    editing: boolean;
    html: string;
    label: string;
    header?: ReactNode;
    fallback?: ReactNode;
    headerActions?: ReactNode;
    pendingHtml?: string | null;
    onEditStart?: () => void;
    onEditSave: (html: string) => void;
    onEditCancel: () => void;
    onApplyPending?: () => void;
};

export function EditableEstimateSection({
    editing,
    html,
    label,
    header,
    fallback,
    headerActions,
    pendingHtml,
    onEditStart,
    onEditSave,
    onEditCancel,
    onApplyPending,
}: EditableEstimateSectionProps) {
    const form = useForm<{ body_html: string }>({
        defaultValues: { body_html: html },
    });
    const { reset, getValues } = form;

    useEffect(() => {
        if (editing) {
            reset({ body_html: html });
        }
    }, [editing, html, reset]);

    const showApplyPending =
        !!pendingHtml && pendingHtml !== html && !!onApplyPending;

    if (editing) {
        return (
            <Box
                sx={{
                    mb: 1,
                    p: 1.5,
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                }}
            >
                {header}
                <FormProvider {...form}>
                    <ResourceContextProvider value="quotes">
                        <EmailRichTextInput
                            source="body_html"
                            label={label}
                            fullWidth
                        />
                    </ResourceContextProvider>
                </FormProvider>
                <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
                    <Button size="small" onClick={onEditCancel}>Cancel</Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => onEditSave(getValues('body_html') || '')}
                    >
                        Done
                    </Button>
                </Stack>
            </Box>
        );
    }

    return (
        <Box
            onClick={onEditStart}
            role={onEditStart ? 'button' : undefined}
            tabIndex={onEditStart ? 0 : undefined}
            onKeyDown={e => {
                if (!onEditStart) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onEditStart();
                }
            }}
            sx={{
                position: 'relative',
                mb: 1,
                p: 1.5,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: onEditStart ? 'pointer' : 'default',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': onEditStart
                    ? {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                        '& .estimate-edit-overlay': { opacity: 1 },
                        '& .estimate-edit-icon': {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                        },
                        '& .estimate-apply-icon': {
                            bgcolor: 'secondary.main',
                            color: 'secondary.contrastText',
                        },
                    }
                    : undefined,
            }}
        >
            {(header || headerActions) && (
                <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}
                    onClick={e => {
                        if ((e.target as HTMLElement).closest('[data-estimate-header-action]')) {
                            e.stopPropagation();
                        }
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>{header}</Box>
                    {headerActions && (
                        <Box data-estimate-header-action onClick={e => e.stopPropagation()}>
                            {headerActions}
                        </Box>
                    )}
                </Box>
            )}
            {html ? (
                <Box
                    sx={{
                        fontSize: 12,
                        lineHeight: 1.6,
                        '& ul': { pl: 2.5, mt: 0, mb: 0 },
                        '& li': { mb: 0.5 },
                        '& p': { m: 0, mb: 0.5 },
                    }}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            ) : (
                fallback
            )}
            {onEditStart && (
                <Box
                    className="estimate-edit-overlay"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        opacity: 0.35,
                        transition: 'opacity 0.15s',
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ pointerEvents: 'auto' }}
                    >
                        {showApplyPending && (
                            <IconButton
                                className="estimate-apply-icon"
                                size="small"
                                aria-label="Apply updated default"
                                title="Apply updated default"
                                onClick={e => {
                                    e.stopPropagation();
                                    onApplyPending?.();
                                }}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: 'background.paper',
                                    color: 'secondary.main',
                                    boxShadow: 1,
                                    transition: 'background-color 0.15s, color 0.15s',
                                }}
                            >
                                <SyncIcon fontSize="small" />
                            </IconButton>
                        )}
                        <IconButton
                            className="estimate-edit-icon"
                            size="small"
                            aria-label={`Edit ${label}`}
                            title={`Edit ${label}`}
                            onClick={e => {
                                e.stopPropagation();
                                onEditStart?.();
                            }}
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: 'background.paper',
                                color: 'primary.main',
                                boxShadow: 1,
                                transition: 'background-color 0.15s, color 0.15s',
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Box>
            )}
        </Box>
    );
}
